import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { CheckCircle2, HelpCircle, Loader2, Play, Save } from 'lucide-react'
import { toast } from 'sonner'

import { triggerExtension } from '@/api/extension'
import { matchQuery, useMarkAppliedMutation } from '@/api/matches'
import { profileQuery } from '@/api/profile'
import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { RouteError } from '@/components/common/route-error'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  extensionQuestionsKey,
  extensionQuestionsQuery,
  useSaveExtensionAnswersMutation,
  type ExtensionQuestion,
  type ExtensionQuestionSet,
} from '@/api/questions'

/**
 * /questions - screening questions the Apply Tool could not answer itself.
 *
 * This is where the extension's report page sends the user
 * (`/questions?matchId=...`; the ids are informational, the list always shows
 * everything still unfinished). Answers are stored locally, then the job is
 * handed straight back to the Apply Tool: the run that collected these
 * questions already ended, so nothing else would ever come back to finish the
 * form. This is why it lives apart from the match drawer's Q&A tab (that one
 * submits to LoopCV).
 */
export const Route = createFileRoute('/_authed/questions')({
  loader: ({ context }) => context.queryClient.ensureQueryData(extensionQuestionsQuery()),
  component: QuestionsPage,
  errorComponent: RouteError,
})

function QuestionsPage() {
  const { data: sets } = useSuspenseQuery(extensionQuestionsQuery())

  return (
    <PageContainer>
      <PageHeader
        title="Screening questions"
        description="Questions a job board asked that the Apply Tool could not fill. Saving sends the job back to the Apply Tool to finish the application."
      />

      {sets.length === 0 ? (
        <EmptyState
          icon={HelpCircle}
          title="Nothing waiting on you"
          description="Everything the Apply Tool parked here has been applied already."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {sets.map((set) => (
            <QuestionSetCard key={set.matchId} set={set} />
          ))}
        </div>
      )}
    </PageContainer>
  )
}

function QuestionSetCard({ set }: { set: ExtensionQuestionSet }) {
  const queryClient = useQueryClient()
  const save = useSaveExtensionAnswersMutation(set.matchId)
  const markApplied = useMarkAppliedMutation()
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...set.answers }))

  // 全部答完但还没申请出去：这条 job 卡在「等扩展重新提交」，给个继续按钮，
  // 不用再保存一遍。
  const dirty = set.questions.some(
    (q) => (values[q.label] ?? '').trim() !== (set.answers[q.label] ?? '').trim(),
  )
  const allAnswered = set.questions.every((q) =>
    Boolean(((values[q.label] ?? '') || (set.answers[q.label] ?? '')).trim()),
  )
  const readyToResume = allAnswered && !dirty
  const unanswered = set.questions.filter(
    (q) => !((values[q.label] ?? '').trim() || (set.answers[q.label] ?? '').trim()),
  ).length

  // 保存答案后必须重新触发扩展：上一次运行停在问题这一步就结束了，不重新
  // 排队这条 job，答案永远不会被填进表单，也就不会有 FORM_APPLIED。
  const resume = useMutation({
    mutationFn: async () => {
      const [match, profile] = await Promise.all([
        queryClient.ensureQueryData(matchQuery(set.matchId)),
        queryClient.ensureQueryData(profileQuery()),
      ])
      if (!match.applyLink) {
        throw new Error('this match has no application link to reopen')
      }
      await triggerExtension(match, profile)
    },
  })

  const resumeToast = {
    onSuccess: () => toast.success('Apply Tool is reopening this job to finish it'),
    onError: (e: Error) =>
      toast.error(
        `Could not reopen the job: ${e.message}. Open it from Matches, or press "Restart process" in the extension popup.`,
      ),
  }

  const onSubmit = () => {
    // 数字类问题先卡格式：job board 只认纯数字，"1-3 years" 会被判 Invalid
    // input，扩展照填就停在那一页，整条申请又回到待答列表。
    const badNumeric = set.questions.filter((q) => {
      if (!isNumericQuestion(q)) return false
      const value = (values[q.label] ?? '').trim() || (set.answers[q.label] ?? '').trim()
      return value !== '' && normalizeNumeric(value) === null
    })
    if (badNumeric.length > 0) {
      toast.error(
        `These need a plain number (for example 3): ${badNumeric.map((q) => q.label).join(' | ')}`,
      )
      return
    }

    const answers: Record<string, string> = {}
    for (const q of set.questions) {
      const value = (values[q.label] ?? '').trim()
      if (!value) continue
      answers[q.label] = isNumericQuestion(q) ? (normalizeNumeric(value) ?? value) : value
    }

    // 工具一次只看得到它卡住的那一页，所以必须整份快照答完才重跑它：少答一题就
    // continue，这次运行只会再停一次，job 又回到待答列表。
    const merged = { ...set.answers, ...answers }
    const missing = set.questions.filter((q) => !(merged[q.label] ?? '').trim()).length
    if (missing > 0) {
      if (Object.keys(answers).length === 0) {
        toast.error(`Answer the remaining ${missing} question${missing === 1 ? '' : 's'} first.`)
        return
      }
      save.mutate(answers, {
        onSuccess: () =>
          toast.success(
            `Saved - ${missing} question${missing === 1 ? '' : 's'} still need an answer before the Apply Tool continues.`,
          ),
        onError: (e) => toast.error(e.message || 'Could not save answers'),
      })
      return
    }

    if (readyToResume) {
      resume.mutate(undefined, resumeToast)
      return
    }
    save.mutate(answers, {
      onSuccess: () => resume.mutate(undefined, resumeToast),
      onError: (e) => toast.error(e.message || 'Could not save answers'),
    })
  }

  return (
    <section className="rounded-[var(--radius-card)] border border-divider bg-surface-base p-4">
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-ink">
          {set.jobTitle || 'Untitled role'}
          {set.company ? `, ${set.company}` : ''}
        </h2>
        <p className="text-xs text-muted">
          {set.questions.length} question{set.questions.length === 1 ? '' : 's'}
          {readyToResume
            ? ' - all answered, not applied yet'
            : unanswered > 0
              ? ` - ${unanswered} still to answer`
              : ''}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {set.questions.map((q) => (
          <QuestionControl
            key={q.label}
            question={q}
            value={values[q.label] ?? ''}
            onChange={(v) => setValues((prev) => ({ ...prev, [q.label]: v }))}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-divider pt-3">
        {allAnswered ? (
          <Button
            variant="secondary"
            onClick={() =>
              markApplied.mutate(set.matchId, {
                onSuccess: () => {
                  // 工具没上报 FORM_APPLIED（比如它判定表单没填好就收工了），但
                  // 用户自己在浏览器里提交成功了 —— 手动补记，列表随即消失。
                  toast.success('Marked as applied')
                  void queryClient.invalidateQueries({ queryKey: extensionQuestionsKey })
                },
                onError: (e) => toast.error(e.message || 'Could not mark applied'),
              })
            }
            disabled={markApplied.isPending}
          >
            {markApplied.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
            Mark as applied
          </Button>
        ) : null}
        <Button onClick={onSubmit} disabled={save.isPending || resume.isPending}>
          {save.isPending || resume.isPending ? (
            <Loader2 className="animate-spin" />
          ) : readyToResume ? (
            <Play />
          ) : (
            <Save />
          )}
          {readyToResume ? 'Continue applying' : 'Save answers'}
        </Button>
      </div>
    </section>
  )
}

/**
 * One question. Questions with options render as a picker - the extension
 * fills radio/checkbox groups by matching an answer string against the option
 * value or its visible label, so we store the option's own value.
 *
 * "How many years…" style questions are numeric fields on the job board, so
 * they get a number input even when the extension reports them as text.
 *
 * ponytail: multi-select questions get a single-choice control; the tool can
 * only type one option per answer string. Split the value when it matters.
 */
function isNumericQuestion(question: ExtensionQuestion): boolean {
  if (question.type === 'number') return true
  if ((question.options?.length ?? 0) > 0) return false
  return /\bhow many\b|\bnumber of\b|\byears?\b|\bmonths?\b/i.test(question.label)
}

/** Bare number for a numeric question, tolerating a trailing unit ("1 year"). */
function normalizeNumeric(value: string): string | null {
  const m = value.trim().match(/^(\d+(?:\.\d+)?)\s*(?:years?|yrs?|months?|mos?)?$/i)
  return m?.[1] ?? null
}

function QuestionControl({
  question,
  value,
  onChange,
}: {
  question: ExtensionQuestion
  value: string
  onChange: (value: string) => void
}) {
  const options = question.options ?? []
  const htmlFor = `q-${question.id || question.label}`

  if (options.length > 0) {
    return (
      <FormField label={question.label} htmlFor={htmlFor}>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={htmlFor}>
            <SelectValue placeholder="Pick an answer" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => {
              const answer = option.value || option.name
              return (
                <SelectItem key={answer} value={answer}>
                  {option.name || option.value}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </FormField>
    )
  }

  return (
    <FormField label={question.label} htmlFor={htmlFor}>
      <Input
        id={htmlFor}
        type={isNumericQuestion(question) ? 'number' : 'text'}
        min={isNumericQuestion(question) ? 0 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  )
}
